import { createApi } from '@reduxjs/toolkit/query/react';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import axiosBaseQuery from 'src/shared/api/api';
import {
  Account,
  Enumerations,
  SellingAccountsResponse,
  SellingAccountType,
} from 'src/shared/models/account';
import { GetAccountStatusResponse } from '@services/account/types';
import {
  CreateAccountPayload,
  CreateAccountResponse,
  CreateManagerAccountPayload,
  CreateManagerAccountResponse,
  ManagerAccountMultiStatusResponse,
  ManagerAccountRegistrationInfo,
  ManagerAccountsPayload,
  UpdateAccountResponse,
  CreateRetailerAccountResponse,
  CreateRetailerAccountPayload,
} from '@services/account/types';
import { CaptchaResponse } from '@models/captcha';
import { SellingProgram } from '@features/account-settings/utils';

const BASE_URL = '/ads-account/api/v1/';
const ACCOUNT_ENDPOINT = 'account';
const RETAILER_ACCOUNT_ENDPOINT = 'retailer-account';
const MANAGER_ACCOUNT_ENDPOINT = 'manager-account';
const MANAGER_ACCOUNTS_ENDPOINT = 'manager-accounts';
const MANAGER_ACCOUNT_REGISTRATION_INFO_ENDPOINT =
  'manager-account-registration-info';
const ACCOUNT_STATUS_ENDPOINT = 'account-status';
const REDUCER_PATH = 'AccountApi';
const SELLING_ACCOUNTS = 'sellingAccounts';
const ENUMERATIONS = 'enumerations';

const getCaptchaHeaders = ({
  sessionToken,
  actionType,
  clientSideContext,
}: CaptchaResponse) => {
  return {
    'Captcha-Session-Token': sessionToken,
    'Captcha-Action-Type': actionType,
    'Captcha-Client-Side-Context': clientSideContext,
  };
};

const getCaptchaBodyFields = ({
  sessionToken,
  actionType,
  clientSideContext,
}: CaptchaResponse) => {
  return {
    captchaSessionToken: sessionToken,
    captchaActionType: actionType,
    captchaClientSideContext: clientSideContext,
  };
};

/**
 *  Account Service
 */
export const accountApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: axiosBaseQuery({
    baseUrl: BASE_URL,
  }),
  tagTypes: ['Account', 'SellingAccount'],
  endpoints: (builder) => ({
    getAccount: builder.query<Account, string>({
      query: (entityId) => ({
        url: `${ACCOUNT_ENDPOINT}/${entityId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      providesTags: (result, error, entityId) => [
        { type: 'Account', id: entityId },
      ],
    }),
    /**
     * The same read as `getAccountStatus`, for polling.
     *
     * A mutation rather than a query because it is polled. Every round sends identical arguments, so
     * a query would answer the rounds that settle nothing from the store instead of asking the
     * service, which is the one thing a poll cannot tolerate. The steering file states this as a
     * convention and the upgrade lane already follows it with `fetchUpgradeStatus`.
     *
     * `getAccountStatus` happens to be safe to poll today only because it sets
     * `keepUnusedDataFor: 0`, which makes correctness depend on a caching detail of a shared query
     * rather than on the semantics of the call. This exists so a caller does not have to know that.
     */
    fetchAccountStatus: builder.mutation<
      GetAccountStatusResponse,
      { globalAccountId: string; managerAccountId?: string }
    >({
      query: ({ globalAccountId, managerAccountId }) => ({
        url: `${ACCOUNT_STATUS_ENDPOINT}/${globalAccountId}${
          managerAccountId ? `?maId=${managerAccountId}` : ''
        }`,
        method: 'GET',
      }),
    }),
    getAccountStatus: builder.query<
      GetAccountStatusResponse,
      { globalAccountId: string; managerAccountId?: string }
    >({
      query: ({ globalAccountId, managerAccountId }) => ({
        url: `${ACCOUNT_STATUS_ENDPOINT}/${globalAccountId}${
          managerAccountId ? `?maId=${managerAccountId}` : ''
        }`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
      providesTags: (result, error, { globalAccountId }) => [
        { type: 'Account', id: globalAccountId },
      ],
    }),
    updateAccount: builder.mutation<
      UpdateAccountResponse,
      { entityId: string; data: Partial<Account> }
    >({
      query: ({ entityId, data }) => ({
        url: `${ACCOUNT_ENDPOINT}/${entityId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { entityId }) => [
        { type: 'Account', id: entityId },
      ],
    }),
    createAccount: builder.mutation<
      CreateAccountResponse,
      { data: CreateAccountPayload; captchaResponse?: CaptchaResponse }
    >({
      query: ({ data, captchaResponse }) => ({
        url: `${ACCOUNT_ENDPOINT}`,
        method: 'POST',
        ...(captchaResponse && {
          headers: getCaptchaHeaders(captchaResponse),
        }),
        // Captcha fields are duplicated in the body because the advertising
        // console (Mons) edge does not forward custom request headers.
        data: captchaResponse
          ? { ...data, ...getCaptchaBodyFields(captchaResponse) }
          : data,
      }),
    }),
    createRetailerAccount: builder.mutation<
      CreateRetailerAccountResponse,
      { data: CreateRetailerAccountPayload; captchaResponse?: CaptchaResponse }
    >({
      query: ({ data, captchaResponse }) => ({
        url: `${RETAILER_ACCOUNT_ENDPOINT}`,
        method: 'POST',
        ...(captchaResponse && {
          headers: getCaptchaHeaders(captchaResponse),
        }),
        data,
      }),
    }),
    createManagerAccount: builder.mutation<
      CreateManagerAccountResponse,
      CreateManagerAccountPayload
    >({
      query: (data) => ({
        url: `${MANAGER_ACCOUNT_ENDPOINT}`,
        method: 'POST',
        data,
      }),
    }),
    /**
     * GRS-backed manager account creation for the ARMS-owned creation page.
     * Separate from `createManagerAccount` above, which serves the DSP admin
     * flow through Arches.
     */
    createManagerAccounts: builder.mutation<
      ManagerAccountMultiStatusResponse,
      { data: ManagerAccountsPayload; captchaResponse?: CaptchaResponse }
    >({
      query: ({ data, captchaResponse }) => ({
        url: `${MANAGER_ACCOUNTS_ENDPOINT}`,
        method: 'POST',
        ...(captchaResponse && {
          headers: getCaptchaHeaders(captchaResponse),
        }),
        // Body copy for the same reason as createAccount: the advertising
        // console edge does not forward custom request headers
        data: captchaResponse
          ? { ...data, ...getCaptchaBodyFields(captchaResponse) }
          : data,
      }),
    }),
    getManagerAccountRegistrationInfo: builder.query<
      ManagerAccountRegistrationInfo,
      void
    >({
      query: () => ({
        url: `${MANAGER_ACCOUNT_REGISTRATION_INFO_ENDPOINT}`,
        method: 'GET',
      }),
    }),
    querySellingAccount: builder.query<
      SellingAccountsResponse,
      { sellingAccountLinkToken?: string; sellingProgram?: SellingAccountType }
    >({
      query: ({ sellingAccountLinkToken, sellingProgram }) => {
        let url = SELLING_ACCOUNTS;
        if (sellingAccountLinkToken && sellingProgram) {
          url = `${url}?sellingAccountLinkToken=${sellingAccountLinkToken}&sellingProgram=${get(
            SellingProgram,
            [sellingProgram],
          )}`;
        }
        return {
          url,
          method: 'GET',
        };
      },
      keepUnusedDataFor: 0,
      providesTags: (result, error, { sellingAccountLinkToken }) => [
        {
          type: 'SellingAccount',
          id: sellingAccountLinkToken ? sellingAccountLinkToken : '',
        },
      ],
    }),
    // Added an example to demonstrate how multiple calls can be done parallely.
    // We can add api calls to the list of promises and check responses for each call to either return response or throw an error.
    // Currently it's not used anywhere.
    multipleQueries: builder.query<Account, string>({
      async queryFn(entityId, _queryApi, _extraOptions, fetchWithBaseQuery) {
        const [getAccountResult] = await Promise.all([
          fetchWithBaseQuery({
            url: `${ACCOUNT_ENDPOINT}/${entityId}`,
            method: 'GET',
          }),
        ]);

        if (!isEmpty(getAccountResult.error)) {
          throw new Error('Fetch account failed');
        }
        return {
          data: getAccountResult.data as Account,
        };
      },
    }),
    getEnumerations: builder.query<
      Enumerations,
      { managerAccountId?: string | null }
    >({
      query: ({ managerAccountId }) => ({
        url: `${ENUMERATIONS}${
          managerAccountId ? `?maId=${managerAccountId}` : ''
        }`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetAccountQuery,
  useGetAccountStatusQuery,
  useLazyGetAccountStatusQuery,
  useFetchAccountStatusMutation,
  useUpdateAccountMutation,
  useCreateAccountMutation,
  useCreateRetailerAccountMutation,
  useCreateManagerAccountMutation,
  useCreateManagerAccountsMutation,
  useGetManagerAccountRegistrationInfoQuery,
  useQuerySellingAccountQuery,
  useLazyQuerySellingAccountQuery,
  useMultipleQueriesQuery,
  useGetEnumerationsQuery,
} = accountApi;
